import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, X } from "lucide-react";
import { IoLogoTiktok } from "react-icons/io5";
import Button from "./Button";

interface HamburgerProps {
  setOpenMenu: (open: boolean) => void;
}

const Hamburger = ({ setOpenMenu }: HamburgerProps) => {
  const hamburgerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (hamburgerRef.current && !hamburgerRef.current.contains(event.target as Node)) {
        setOpenMenu(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hamburgerRef, setOpenMenu]);

  return (
    <div 
      ref={hamburgerRef} 
      className="rounded-lg border-2 border-primaryOrange bg-gray-200 bg-opacity-95 py-5 px-4 z-50 fixed bottom-20 right-20 mr-4 w-60 font-light"
      role="dialog"
      aria-label="Mobile navigation menu"
      aria-modal="true"
    >
      <button
        onClick={() => setOpenMenu(false)}
        className="absolute right-2 rounded-full bg-primaryOrange text-sm p-1 focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:ring-offset-2"
        aria-label="Close navigation menu"
        type="button"
      >
        <X size={16} aria-hidden="true" />
      </button>

      <nav className="flex flex-col text-xl text-center space-y-4" role="navigation" aria-label="Main navigation">
        <Link
          id="menu-home"
          to="/home"
          className="hover:underline focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:rounded-md py-1"
          onClick={() => setOpenMenu(false)}
        >
          Home
        </Link>
        <Link
          id="menu-about"
          to="/about"
          className="hover:underline focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:rounded-md py-1"
          onClick={() => setOpenMenu(false)}
        >
          About
        </Link>
        <Link
          id="menu-solutions"
          to="/solutions"
          className="hover:underline focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:rounded-md py-1"
          onClick={() => setOpenMenu(false)}
        >
          Solutions
        </Link>
        <Button
          href="https://form.typeform.com/to/QGaX0cpb"
          target="_blank"
          variant="primary"
          type="hero"
          size="sm"
          aria-label="Partner with Vespid - Opens partnership form in new tab"
        >
          Partner With Us
        </Button>
        <div className="flex flex-row justify-center space-x-4" role="group" aria-label="Social media links">
          <a
            href="https://www.instagram.com/vespid_my/"
            target="_blank"
            rel="noreferrer noopener"
            className="focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:rounded-md p-1"
            aria-label="Follow Vespid on Instagram - Opens in new tab"
          >
            <Instagram className="w-6 h-6 inline" aria-hidden="true" />
          </a>
          <a 
            href="https://www.linkedin.com/company/vespid" 
            target="_blank" 
            rel="noreferrer noopener"
            className="focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:rounded-md p-1"
            aria-label="Connect with Vespid on LinkedIn - Opens in new tab"
          >
            <Linkedin className="w-6 h-6 inline" aria-hidden="true" />
          </a>
          <a 
            href="https://www.tiktok.com/@vespid_my" 
            target="_blank" 
            rel="noreferrer noopener"
            className="focus:outline-none focus:ring-2 focus:ring-primaryYellow focus:rounded-md p-1"
            aria-label="Follow Vespid on TikTok - Opens in new tab"
          >
            <IoLogoTiktok className="w-6 h-6 inline" aria-hidden="true" />
          </a>
        </div>

      </nav>
    </div>
  );
};

export default Hamburger;
